import { Link, useLocation } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useLocale } from "@/i18n/context";
import { localizedPath } from "@/i18n/routes";
import {
  getGuideByPath,
  relatedGuides,
  termAnchor,
  GUIDE_UI,
} from "@/content/guides/registry";
import type { GuideBlock } from "@/content/guides/registry";

function slugifyHeading(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function BlockView({ block }: { block: GuideBlock }) {
  if ("p" in block) return <p>{block.p}</p>;
  if ("h3" in block) return <h3>{block.h3}</h3>;
  if ("ul" in block)
    return (
      <ul>
        {block.ul.map((item: string, i: number) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    );
  if ("ol" in block)
    return (
      <ol>
        {block.ol.map((item: string, i: number) => (
          <li key={i}>{item}</li>
        ))}
      </ol>
    );
  if ("steps" in block)
    return (
      <ol className="not-prose my-6 space-y-4">
        {block.steps.map(
          (step: { title: string; text: string }, i: number) => (
            <li key={i} className="flex gap-4">
              <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                {i + 1}
              </span>
              <span>
                <span className="block font-semibold text-foreground">
                  {step.title}
                </span>
                <span className="text-muted-foreground">{step.text}</span>
              </span>
            </li>
          )
        )}
      </ol>
    );
  if ("table" in block)
    return (
      <div className="not-prose my-6 overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border">
              {block.table.head.map((h: string, i: number) => (
                <th
                  key={i}
                  className="px-3 py-2 text-left font-semibold text-foreground"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.table.rows.map((row: string[], ri: number) => (
              <tr key={ri} className="border-b border-border/50 align-top">
                {row.map((cell: string, ci: number) => (
                  <td
                    key={ci}
                    className={
                      ci === 0
                        ? "px-3 py-2 font-medium text-foreground"
                        : "px-3 py-2 text-muted-foreground"
                    }
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  if ("callout" in block)
    return (
      <div className="not-prose my-6 rounded-2xl border border-primary/20 bg-primary/5 p-5">
        {block.callout.title ? (
          <p className="mb-1 font-semibold text-foreground">
            {block.callout.title}
          </p>
        ) : null}
        <p className="text-sm leading-relaxed text-muted-foreground">
          {block.callout.text}
        </p>
      </div>
    );
  if ("terms" in block)
    return (
      <dl className="not-prose my-6 space-y-5">
        {block.terms.map(
          (t: { term: string; alternateName?: string; definition: string }) => (
            <div
              key={t.term}
              id={termAnchor(t.term)}
              className="scroll-mt-24 border-l-2 border-primary/30 pl-4"
            >
              <dt className="font-semibold text-foreground">
                {t.term}
                {t.alternateName ? (
                  <span className="font-normal text-muted-foreground">
                    {" "}
                    ({t.alternateName})
                  </span>
                ) : null}
              </dt>
              <dd className="mt-1 text-muted-foreground leading-relaxed">
                {t.definition}
              </dd>
            </div>
          )
        )}
      </dl>
    );
  if ("sources" in block)
    return (
      <ul className="not-prose my-4 space-y-2">
        {block.sources.map(
          (s: { label: string; href: string }, i: number) => (
            <li key={i}>
              <a
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline"
              >
                {s.label} ↗
              </a>
            </li>
          )
        )}
      </ul>
    );
  return null;
}

const GuideArticle = () => {
  const { pathname } = useLocation();
  const { locale } = useLocale();
  const entry = getGuideByPath(pathname);
  const ui = GUIDE_UI[locale as "sl" | "en"] ?? GUIDE_UI.sl;
  const homePath = localizedPath("home", locale);

  if (!entry || entry.kind !== "guide") {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="container mx-auto px-4 py-24 text-center">
          <p className="text-muted-foreground">404</p>
        </div>
        <Footer />
      </div>
    );
  }

  const g = entry.content;
  const hubPath = locale === "en" ? "/en/guide" : "/vodnik";
  const related = relatedGuides(locale, entry.group);

  return (
    <div className="min-h-screen">
      <Header />
      <div className="pt-16 bg-background">
        <article className="container mx-auto px-5 py-14 sm:px-8 sm:py-16 max-w-3xl">
          {/* Breadcrumb */}
          <nav
            aria-label="Breadcrumb"
            className="mb-6 flex flex-wrap items-center gap-2 text-sm text-muted-foreground"
          >
            <Link to={homePath} className="hover:text-primary">
              {ui.home}
            </Link>
            <span aria-hidden>/</span>
            <Link to={hubPath} className="hover:text-primary">
              {ui.hubBreadcrumb}
            </Link>
            <span aria-hidden>/</span>
            <span className="text-foreground">{g.breadcrumb}</span>
          </nav>

          <p className="section-eyebrow mb-3">{g.eyebrow}</p>
          <h1 className="section-title text-left mb-4">{g.h1}</h1>
          <p className="mb-2 text-xs text-muted-foreground">
            {ui.updated}: {entry.meta?.dateModified}
          </p>
          <p className="mb-8 text-lg leading-relaxed text-muted-foreground">
            {g.intro}
          </p>

          {/* On this page */}
          {g.sections.length > 2 ? (
            <nav
              aria-label={ui.onThisPage}
              className="mb-10 rounded-2xl border border-border/60 bg-card/60 p-5"
            >
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-foreground/70">
                {ui.onThisPage}
              </p>
              <ul className="space-y-2">
                {g.sections.map((s: { h2: string }) => (
                  <li key={s.h2}>
                    <a
                      href={`#${slugifyHeading(s.h2)}`}
                      className="text-sm text-muted-foreground hover:text-primary"
                    >
                      {s.h2}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          ) : null}

          <div className="prose prose-lg max-w-none prose-headings:tracking-tight prose-a:text-primary">
            {g.sections.map(
              (section: { h2: string; blocks: GuideBlock[] }) => (
                <section key={section.h2} className="mb-8">
                  <h2 id={slugifyHeading(section.h2)}>{section.h2}</h2>
                  {section.blocks.map((block, i) => (
                    <BlockView key={i} block={block} />
                  ))}
                </section>
              )
            )}

            {/* FAQ */}
            <section className="mb-4">
              <h2>{ui.faqTitle}</h2>
              {g.faq.map((item: { question: string; answer: string }) => (
                <div key={item.question} className="mb-5">
                  <h3 className="mb-1">{item.question}</h3>
                  <p className="mt-0 text-muted-foreground">{item.answer}</p>
                </div>
              ))}
            </section>
          </div>

          <p className="mt-8 text-xs text-muted-foreground">{ui.disclaimer}</p>

          {/* Related */}
          {related.length ? (
            <div className="mt-10 border-t border-border/60 pt-8">
              <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-foreground/70">
                {ui.related}
              </p>
              <ul className="grid gap-3 sm:grid-cols-2">
                {related.map((r) => (
                  <li key={r.path}>
                    <Link
                      to={r.path}
                      className="block rounded-xl border border-border/60 bg-card/60 p-4 text-sm font-medium text-foreground transition-colors hover:border-primary/40 hover:text-primary"
                    >
                      {r.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* CTA */}
          <div className="mt-10 rounded-2xl border border-primary/20 bg-primary/5 p-6 text-center">
            <p className="text-lg font-semibold text-foreground">
              {ui.ctaTitle}
            </p>
            <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground">
              {ui.ctaText}
            </p>
            <Link
              to={homePath}
              className="mt-4 inline-flex items-center justify-center rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
            >
              {ui.ctaButton}
            </Link>
          </div>
        </article>
      </div>
      <Footer />
    </div>
  );
};

export default GuideArticle;
